/**
 * NewsletterPopup
 * Shows a modal overlay on first visit prompting email signup.
 * Dismisses on submit or close. Won't show again for 7 days (localStorage).
 */

import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../services/api';
import './NewsletterPopup.css';

const STORAGE_KEY = 'micahlynise_popup_dismissed';
const DISMISS_DAYS = 7;
const SHOW_DELAY_MS = 1500; // Show after 1.5 seconds

const NewsletterPopup: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const dismissedAt = new Date(dismissed).getTime();
      const now = Date.now();
      if (now - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
        return; // Still within cooldown
      }
    }

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await apiRequest('/api/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="newsletter-popup__overlay" onClick={dismiss} role="dialog" aria-modal="true" aria-label="Newsletter signup">
      <div className="newsletter-popup" onClick={(e) => e.stopPropagation()}>
        <button className="newsletter-popup__close" onClick={dismiss} aria-label="Close">
          ×
        </button>

        {submitted ? (
          <div className="newsletter-popup__success">
            <p className="newsletter-popup__success-icon">✓</p>
            <h2 className="newsletter-popup__heading">You're in.</h2>
            <button className="newsletter-popup__btn" onClick={dismiss}>Continue</button>
          </div>
        ) : (
          <>
            <p className="newsletter-popup__eyebrow">Be the first to know</p>
            <h2 className="newsletter-popup__heading">Stay Connected.</h2>
            <p className="newsletter-popup__body">
              New releases, studio notes, and exclusive offers.
            </p>
            <form className="newsletter-popup__form" onSubmit={handleSubmit}>
              <input
                type="email"
                className="newsletter-popup__input"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
              <button type="submit" className="newsletter-popup__btn" disabled={loading}>
                {loading ? '...' : 'Join'}
              </button>
            </form>
            <p className="newsletter-popup__disclaimer">No spam. Unsubscribe anytime.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterPopup;
